/**
 * Image Processing Utilities
 * Handles client-side image validation, optimization, and resizing
 */

// Allowed image formats
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const MAX_DIMENSION = 800; // Maximum width or height in pixels

/**
 * Validates image file type and MIME type
 * @param {File} file - The image file to validate
 * @returns {Object} - { isValid: boolean, error: string | null }
 */
export const validateImageFile = file => {
  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));

  if (!hasValidExtension) {
    return {
      isValid: false,
      error: 'Invalid file type. Please upload JPG or PNG images only.',
    };
  }

  // Check MIME type
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: 'Invalid image format. Please upload JPG or PNG images only.',
    };
  }

  return { isValid: true, error: null };
};

/**
 * Resizes image to max 800px dimension while maintaining aspect ratio
 * Only scales down - if image is already smaller, returns original
 * @param {File} file - The image file to process
 * @returns {Promise<Blob>} - Optimized image blob
 */
export const optimizeImage = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = e => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        try {
          const { width, height } = img;

          // Calculate new dimensions (only scale down if needed)
          let newWidth = width;
          let newHeight = height;

          if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
            // Scale down while maintaining aspect ratio
            if (width > height) {
              newWidth = MAX_DIMENSION;
              newHeight = Math.round((height * MAX_DIMENSION) / width);
            } else {
              newHeight = MAX_DIMENSION;
              newWidth = Math.round((width * MAX_DIMENSION) / height);
            }
          }

          // Set canvas dimensions
          canvas.width = newWidth;
          canvas.height = newHeight;

          // Draw resized image
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          // Convert to blob with appropriate format
          const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
          const quality = 0.92; // High quality for JPEG

          canvas.toBlob(
            blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create image blob'));
              }
            },
            mimeType,
            quality,
          );
        } catch (error) {
          reject(new Error(`Image processing error: ${error.message}`));
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image. The file may be corrupted or not a valid image.'));
      };

      // Set image source from FileReader result
      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file. Please try again.'));
    };

    // Read file as data URL
    reader.readAsDataURL(file);
  });
};

/**
 * Validates and optimizes an image file
 * @param {File} file - The image file to process
 * @returns {Promise<Object>} - { blob: Blob, fileName: string, mimeType: string }
 */
export const processImageForUpload = async file => {
  // Validate file
  const validation = validateImageFile(file);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Optimize image (resize if needed)
  const optimizedBlob = await optimizeImage(file);

  return {
    blob: optimizedBlob,
    fileName: file.name,
    mimeType: file.type,
    originalSize: file.size,
    optimizedSize: optimizedBlob.size,
  };
};

/**
 * Get file extension from MIME type
 * @param {string} mimeType - MIME type (e.g., 'image/jpeg')
 * @returns {string} - File extension without dot (e.g., 'jpg')
 */
export const getExtensionFromMimeType = mimeType => {
  const mimeToExt = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
  };
  return mimeToExt[mimeType] || 'jpg';
};

/**
 * Gets image dimensions from file
 * @param {File} file - The image file
 * @returns {Promise<Object>} - { width: number, height: number }
 */
export const getImageDimensions = file => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(img.src);
    };

    img.src = URL.createObjectURL(file);
  });
};
